import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  PERMISSION_GROUPS,
  PERMISSIONS,
  VIEW_ACTIVITY_PERMISSION,
} from "../../utils/roleAccess";

const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,30}$/;
const PASSWORD_MIN = 8;

/** Random temporary password: letters, digits and a symbol, 12 chars. */
export const generateTempPassword = (length = 12) => {
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const symbols = "!@#$%*?";
  const all = lower + upper + digits + symbols;
  const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
  const required = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  const rest = Array.from({ length: Math.max(length, 8) - required.length }, () => pick(all));
  return [...required, ...rest].sort(() => Math.random() - 0.5).join("");
};

const fieldSx = {
  "& .MuiInputBase-input": { color: "#142C5C" },
  "& .MuiFormLabel-root.Mui-focused": { color: "#142C5C" },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#142C5C" },
};

const emptyForm = {
  username: "",
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  permissions: [],
};

/**
 * Create / edit a restricted admin account.
 *
 * Create mode asks for a temporary password (typed or generated). Edit mode
 * never touches the password — use "Reset password" on the table for that.
 *
 * onSubmit(payload) must return a promise; the modal closes on success.
 */
const AdminAccountFormModal = ({ open, onClose, onSubmit, account = null }) => {
  const isEdit = Boolean(account);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSubmitting(false);
    setShowPassword(false);
    if (account) {
      setForm({
        username: account.username || "",
        email: account.email || "",
        firstName: account.firstName || "",
        lastName: account.lastName || "",
        password: "",
        permissions: account.permissions || [],
      });
    } else {
      setForm({ ...emptyForm, password: generateTempPassword() });
      setShowPassword(true);
    }
  }, [open, account]);

  const set = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const togglePermission = (key) =>
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((k) => k !== key)
        : [...prev.permissions, key],
    }));

  const setGroup = (group, checked) =>
    setForm((prev) => {
      const keys = group.items.map((item) => item.key);
      const without = prev.permissions.filter((k) => !keys.includes(k));
      return { ...prev, permissions: checked ? [...without, ...keys] : without };
    });

  const pagePermissionCount = useMemo(
    () => form.permissions.filter((k) => k !== PERMISSIONS.VIEW_ACTIVITY).length,
    [form.permissions]
  );

  const validate = () => {
    if (!USERNAME_PATTERN.test(form.username.trim())) {
      return "Username must be 3-30 characters: letters, numbers, periods, underscores or hyphens.";
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return "Enter a valid email address or leave it blank.";
    }
    if (!isEdit && form.password.length < PASSWORD_MIN) {
      return `Temporary password must be at least ${PASSWORD_MIN} characters.`;
    }
    if (pagePermissionCount === 0) {
      return "Select at least one page this account can access.";
    }
    return "";
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        username: form.username.trim().toLowerCase(),
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        permissions: form.permissions,
      };
      if (!isEdit) payload.password = form.password;
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: "#142C5C", fontWeight: 700 }}>
        {isEdit ? `Edit account @${account.username}` : "New admin account"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 0.5 }}>
          <Typography variant="subtitle2" sx={{ color: "#142C5C" }}>
            Login details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Username"
              required
              value={form.username}
              onChange={set("username")}
              disabled={submitting}
              helperText="Used to sign in to the admin panel"
              autoComplete="off"
              sx={fieldSx}
            />
            <TextField
              label="Email (optional)"
              type="email"
              value={form.email}
              onChange={set("email")}
              disabled={submitting}
              helperText="For your records only; no email is sent"
              autoComplete="off"
              sx={fieldSx}
            />
            <TextField
              label="First name (optional)"
              value={form.firstName}
              onChange={set("firstName")}
              disabled={submitting}
              sx={fieldSx}
            />
            <TextField
              label="Last name (optional)"
              value={form.lastName}
              onChange={set("lastName")}
              disabled={submitting}
              sx={fieldSx}
            />
            {!isEdit && (
              <TextField
                label="Temporary password"
                required
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={set("password")}
                disabled={submitting}
                autoComplete="new-password"
                helperText="They must change it on first sign-in. Share it with them securely."
                sx={{ ...fieldSx, gridColumn: { sm: "1 / -1" } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate a new one">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, password: generateTempPassword() }));
                            setShowPassword(true);
                          }}
                          disabled={submitting}
                        >
                          <AutorenewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((v) => !v)}
                        disabled={submitting}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ color: "#142C5C" }}>
              Pages this account can access
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Everything not ticked stays hidden from the sidebar and is blocked by the
              API. Users, analytics, settings, dance, rap and anti-cheat are always
              admin-only.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            {PERMISSION_GROUPS.map((group) => {
              const keys = group.items.map((item) => item.key);
              const checkedCount = keys.filter((k) => form.permissions.includes(k)).length;
              return (
                <Box
                  key={group.id}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    p: 1.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={checkedCount === keys.length}
                        indeterminate={checkedCount > 0 && checkedCount < keys.length}
                        onChange={(e) => setGroup(group, e.target.checked)}
                        disabled={submitting}
                        sx={{ color: "#142C5C", "&.Mui-checked": { color: "#142C5C" } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 600, color: "#142C5C" }}>
                        {group.label}
                      </Typography>
                    }
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", pl: 1 }}>
                    {group.items.map((item) => (
                      <Tooltip
                        key={item.key}
                        title={item.description || ""}
                        placement="right"
                        disableHoverListener={!item.description}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={form.permissions.includes(item.key)}
                              onChange={() => togglePermission(item.key)}
                              disabled={submitting}
                              sx={{ color: "#142C5C", "&.Mui-checked": { color: "#142C5C" } }}
                            />
                          }
                          label={<Typography variant="body2">{item.label}</Typography>}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Divider />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 1.5,
              backgroundColor: "#fafafa",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600, color: "#142C5C" }}>
                {VIEW_ACTIVITY_PERMISSION.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {VIEW_ACTIVITY_PERMISSION.description}
              </Typography>
            </Box>
            <Switch
              checked={form.permissions.includes(PERMISSIONS.VIEW_ACTIVITY)}
              onChange={() => togglePermission(PERMISSIONS.VIEW_ACTIVITY)}
              disabled={submitting}
            />
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          sx={{ background: "#142C5C", "&:hover": { background: "#142C5C" } }}
        >
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Create account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminAccountFormModal;
